/**
 * Floating Copilot launcher button — bottom-right on every authenticated
 * page. Click ALWAYS opens the drawer (no navigation, no redirects). If
 * no tenant context exists, the drawer renders an in-place tenant picker
 * instead of bouncing the user to another route.
 *
 * Tenant detection (highest priority first):
 *   1. user.tenant_id from the auth store (school admins / teachers).
 *   2. /admin/schools/:tenantId or /school/:tenantId in window.location.pathname
 *      — super-admins drilling into a school inherit that tenant context.
 *   3. localStorage('eventflow_last_tenant') — remembered from a previous session.
 *   4. /api/school/tenants list shown as a small picker inside the drawer.
 *
 * Hidden for students/parents (the spec requires them to see a different,
 * stripped-down assistant inside the lessons UI, not this admin drawer).
 */
import { useState, useMemo, useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useAuthStore } from '@/lib/store';
import { Button } from '@/components/ui/button';
import { Sheet, SheetContent, SheetHeader, SheetTitle } from '@/components/ui/sheet';
import { Sparkles, X, AlertCircle } from 'lucide-react';
import api from '@/lib/api';
import CopilotChat from './CopilotChat';
import CopilotFreeChat from './CopilotFreeChat';
import CopilotTierPanel from './CopilotTierPanel';

// Pull a tenant id out of common path patterns the admin navigates through.
function tenantFromUrl() {
  if (typeof window === 'undefined') return '';
  const path = window.location.pathname || '';
  // /admin/schools/:tenantId/...
  const m1 = path.match(/\/admin\/schools\/([a-f0-9]{20,})/i);
  if (m1) return m1[1];
  // /school/:tenantId/...
  const m2 = path.match(/\/school\/([a-f0-9]{20,})/i);
  if (m2) return m2[1];
  return '';
}

export const CopilotDrawer = () => {
  const [open, setOpen] = useState(false);
  const { userType, user, isAuthenticated } = useAuthStore();
  const [pickedTenant, setPickedTenant] = useState('');
  const [mode, setMode] = useState('free');

  // Which half of Copilot this user gets. The server is the authority
  // (core/rbac_hierarchy.py) — this only decides what we render.
  const { data: tier } = useQuery({
    queryKey: ['copilot-tier'],
    queryFn: () => api.get('/copilot/tier').then((r) => r.data).catch(() => null),
    enabled: isAuthenticated,
    staleTime: 300_000,
  });
  const isPro = tier?.tier === 'pro';

  useEffect(() => {
    if (isPro) setMode('pro');
  }, [isPro]);

  // Refresh tenant detection whenever the drawer is opened or the URL changes
  const [urlTenant, setUrlTenant] = useState(() => tenantFromUrl());
  useEffect(() => {
    if (!open) return;
    setUrlTenant(tenantFromUrl());
  }, [open]);

  // Iter-372 — listen for a global "open Copilot with this prompt" event.
  // Other widgets (e.g. CopilotBriefingWidget) dispatch this when an admin
  // clicks "Ask Copilot" on a finding so the drawer flies open with the
  // prompt pre-filled.
  useEffect(() => {
    const onOpen = (e) => {
      setOpen(true);
      const text = e?.detail?.prompt || '';
      if (!text) return;
      // Wait a tick so CopilotChat is mounted before we hand the prompt off.
      setTimeout(() => {
        window.dispatchEvent(new CustomEvent('copilot:prefill', {
          detail: { prompt: text, autoSend: !!e?.detail?.autoSend },
        }));
      }, 200);
    };
    window.addEventListener('copilot:open', onOpen);
    return () => window.removeEventListener('copilot:open', onOpen);
  }, []);

  const tenantId = useMemo(() => {
    return (user?.tenant_id || user?.tenantId || '')
        || urlTenant
        || pickedTenant
        || (typeof window !== 'undefined' ? localStorage.getItem('eventflow_last_tenant') : '')
        || '';
  }, [user, urlTenant, pickedTenant]);

  // Remember the tenant we end up using so the NEXT navigation
  // ("admin home" → click Copilot) doesn't drop the context.
  useEffect(() => {
    if (tenantId && typeof window !== 'undefined') {
      try { localStorage.setItem('eventflow_last_tenant', tenantId); } catch (_e) { /* ignore */ }
    }
  }, [tenantId]);

  if (!isAuthenticated) return null;

  const role = (userType === 'admin' && !user?.tenant_id) ? 'super_admin'
                : (userType === 'admin') ? 'school_admin' : userType;

  return (
    <>
      <button
        onClick={() => setOpen(true)}
        data-testid="copilot-drawer-launcher"
        className="fixed bottom-6 right-6 z-40 flex items-center gap-2 pl-3 pr-4 py-2.5 rounded-full bg-gradient-to-br from-violet-600 to-fuchsia-600 text-white shadow-lg hover:shadow-xl hover:scale-105 transition"
        aria-label="Open FWM Copilot"
      >
        <Sparkles className="h-4 w-4" />
        <span className="text-sm font-medium">Copilot</span>
      </button>

      <Sheet open={open} onOpenChange={setOpen}>
        <SheetContent
          side="right"
          className="w-full sm:w-[460px] p-0 flex flex-col"
          data-testid="copilot-drawer"
        >
          <SheetHeader className="border-b px-4 py-3 flex flex-row items-center justify-between space-y-0">
            <SheetTitle className="flex items-center gap-2 text-base">
              <Sparkles className="h-4 w-4 text-violet-600" />
              FWM Copilot
              <span className={`text-[10px] px-1.5 py-0.5 rounded-full font-medium ${
                isPro ? 'bg-amber-100 text-amber-800' : 'bg-violet-100 text-violet-700'}`}
                    data-testid="copilot-tier-badge">
                {isPro ? 'Pro' : 'Free'}
              </span>
              {isPro && tenantId && (
                <span className="text-[10px] font-normal text-slate-400 truncate max-w-[110px]">
                  · {tenantId.slice(-8)}
                </span>
              )}
            </SheetTitle>
            <Button size="icon" variant="ghost" onClick={() => setOpen(false)}
              data-testid="copilot-drawer-close">
              <X className="h-4 w-4" />
            </Button>
          </SheetHeader>
          <div className="flex-1 overflow-hidden flex flex-col">
            {isPro && (
              <div className="flex border-b text-xs" data-testid="copilot-mode-tabs">
                <button onClick={() => setMode('free')} data-testid="copilot-mode-free"
                        className={`flex-1 py-2 font-medium transition-colors ${
                          mode === 'free' ? 'text-violet-700 border-b-2 border-violet-600'
                                          : 'text-slate-400 hover:text-slate-600'}`}>
                  Ask (free)
                </button>
                <button onClick={() => setMode('pro')} data-testid="copilot-mode-pro"
                        className={`flex-1 py-2 font-medium transition-colors ${
                          mode === 'pro' ? 'text-amber-700 border-b-2 border-amber-500'
                                         : 'text-slate-400 hover:text-slate-600'}`}>
                  Pro · take action
                </button>
              </div>
            )}

            {isPro && mode === 'pro' ? (
              tenantId ? (
                <CopilotChat tenantId={tenantId} userRole={role} />
              ) : (
                <TenantPicker userRole={role} onPick={(tid) => setPickedTenant(tid)} />
              )
            ) : (
              <div className="flex-1 overflow-y-auto flex flex-col">
                <div className="flex-1 min-h-[280px] flex flex-col">
                  <CopilotFreeChat />
                </div>
                <div className="border-t bg-slate-50/70">
                  <CopilotTierPanel tier={tier} compact />
                </div>
              </div>
            )}
          </div>
        </SheetContent>
      </Sheet>
    </>
  );
};

/**
 * In-drawer tenant picker. Super-admins see a live list of schools they
 * can pick from. Other staff who somehow lost their context see a
 * friendly message + a manual id input as a fallback.
 */
const TenantPicker = ({ userRole, onPick }) => {
  const [manual, setManual] = useState('');
  const isSuper = userRole === 'super_admin';

  const { data, isLoading, error } = useQuery({
    queryKey: ['copilot-tenant-list'],
    queryFn: () => api.get('/school/tenants').then(r => r.data),
    enabled: isSuper,
    staleTime: 60_000,
    retry: 1,
  });

  const tenants = Array.isArray(data) ? data : (data?.tenants || []);

  return (
    <div className="p-4 space-y-3" data-testid="copilot-tenant-picker">
      <div className="flex items-start gap-2 text-xs bg-amber-50 border border-amber-200 rounded p-2 text-amber-800">
        <AlertCircle className="h-4 w-4 shrink-0 mt-0.5" />
        <span>
          Pick a school context for Copilot. It uses this tenant to scope every read
          and proposal so nothing crosses boundaries.
        </span>
      </div>

      {isSuper && (
        <>
          {isLoading && <p className="text-xs text-slate-400">Loading schools…</p>}
          {error && <p className="text-xs text-rose-600">Could not load schools: {error.message}</p>}
          {!isLoading && tenants.length > 0 && (
            <div className="space-y-1 max-h-[320px] overflow-auto pr-1">
              {tenants.map((t) => (
                <button
                  key={t._id || t.id}
                  onClick={() => onPick(String(t._id || t.id))}
                  data-testid={`copilot-pick-tenant-${t._id || t.id}`}
                  className="w-full text-left p-2 rounded border border-slate-200 hover:border-violet-300 hover:bg-violet-50 transition text-sm"
                >
                  <div className="font-medium truncate">{t.name || '(unnamed)'}</div>
                  <div className="text-[11px] text-slate-500 truncate">
                    {t.code || ''} · {t.city || ''} {t.country ? `· ${t.country}` : ''}
                  </div>
                </button>
              ))}
            </div>
          )}
          {!isLoading && tenants.length === 0 && !error && (
            <p className="text-xs text-slate-400">No schools available yet.</p>
          )}
        </>
      )}

      <div>
        <label className="block text-[11px] text-slate-500 mb-1">
          {isSuper ? 'Or paste a tenant id' : 'Tenant id'}
        </label>
        <input
          className="w-full border rounded h-9 px-2 text-sm"
          placeholder="Paste tenant id and press Enter"
          value={manual}
          onChange={(e) => setManual(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter' && manual.trim()) {
              onPick(manual.trim());
            }
          }}
          data-testid="copilot-tenant-input"
        />
      </div>
    </div>
  );
};

export default CopilotDrawer;
